import React from "react";
import axios from "axios";
import AddIcon from "@mui/icons-material/Add";
import TabsThree from "../requests/shared/helpers/TabsThree";
import PersonAvatar from "../home/schedule_components/PersonAvatar";
import { getDefaultAxiosConfig } from "../../../utilities/requests";
import AssignTrackToEmployeeModal from "./AssignTrackToEmployeeModal";
import BulkAssignTrackModal from "./BulkAssignTrackModal";
import CheckpointReviewModal from "./CheckpointReviewModal";
import { formatDateReadableWithDayOfWeek } from "../requests/utilities/helpers";
import InfoIcon from "@mui/icons-material/Info";
import Tooltip from "@mui/material/Tooltip";
import Rating from "@mui/material/Rating";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { getRightworkAPI } from "../requests/utilities/requests";
import { postRightworkAPI } from "../requests/utilities/requests";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CompleteTrack from "../take_training_employee/CompleteTrack";
import WarningIcon from "@mui/icons-material/Warning";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import {
  API_get_learning_tracks,
  API_get_learning_track_enrollment_detail_for_employee,
  API_post_learning_section_completed,
  API_post_learning_block_user_passed,
  API_get_role_names,
  API_get_pending_form_grades_for_store,
} from "../../../constants";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { objectToCSV, downloadCSV } from "../../../utilities/csvExport";
import UnassignTrackFromEmployeeModal from "./UnassignTrackFromEmployeeModal";
import TrainingBreadcrumb from "./TrainingBreadcrumb";
import RecordsDisplay from "./RecordsDisplay";
import TrackProgressByTrackDisplay from "./TrackProgressByTrackDisplay";
import { formatName, getCardColor2 } from "../../../helpers/helpers";
import {
  useTrackColumnSizing,
  useStickyEdgeShadow,
  TrackMatrixHeaderRow,
  STICKY_COL_WIDTH,
} from "./trackMatrixShared";

const TRAINING_STATUS_META = {
  complete: {
    label: "Complete",
    legendClass: "bg-emerald-600 border-emerald-600",
    circleClass: "bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700",
  },
  in_progress: {
    label: "In progress",
    legendClass: "bg-violet-600 border-violet-600",
    circleClass: "bg-violet-600 border-violet-600 text-white hover:bg-violet-700",
  },
  overdue: {
    label: "Overdue",
    legendClass: "bg-red-600 border-red-600",
    circleClass: "bg-red-600 border-red-600 text-white hover:bg-red-700",
  },
  blocked: {
    label: "Blocked",
    legendClass: "bg-amber-500 border-amber-500",
    circleClass: "bg-amber-500 border-amber-500 text-white hover:bg-amber-600",
  },
  not_assigned: {
    label: "Not assigned",
    legendClass: "bg-slate-300 border-slate-300",
    circleClass: "bg-slate-200 border-slate-300 text-slate-500 hover:bg-slate-300",
  },
};

const STATUS_LEGEND_ORDER = [
  "complete",
  "in_progress",
  "overdue",
  "blocked",
  "not_assigned",
];

const getTrackProgress = (enrollment) => {
  if (enrollment?.completed_at && !enrollment?.total_sections_count) return 100;
  if (!enrollment?.total_sections_count) return 0;
  return (
    (enrollment.sections_completed_count / enrollment.total_sections_count) *
    100
  );
};

const getTodayUTC = () => {
  const now = new Date();
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
};

const isEnrollmentOverdue = (enrollment, progress) => {
  if (!enrollment?.effective_due_date || progress >= 100) return false;
  return enrollment.effective_due_date < getTodayUTC();
};

const getTrainingStatusKey = (enrollment) => {
  if (!enrollment) return "not_assigned";
  const progress = getTrackProgress(enrollment);
  if (enrollment.is_blocked && enrollment.blocked_by_type) return "blocked";
  if (enrollment.completed_at || progress >= 100) return "complete";
  if (isEnrollmentOverdue(enrollment, progress)) return "overdue";
  return "in_progress";
};

const formatEnrollmentDueDate = (dueDate) => {
  if (!dueDate) return null;
  return new Date(dueDate).toLocaleDateString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
};

function TrainingStoreDisplay(props) {
  const [activeTab, setActiveTab] = React.useState("By Employee");

  // Deeper drilldown crumb reported by the active tab's child view (e.g. the
  // selected record in "By Record" or track in "By Track"). null at list level.
  // Lets the header breadcrumb read like "Back Bay › By Record › Quiz 1".
  const [activeDetailCrumb, setActiveDetailCrumb] = React.useState(null);
  const handleDetailChange = React.useCallback(
    (detail) => setActiveDetailCrumb(detail),
    []
  );
  const clearActiveDetail = () => {
    if (activeDetailCrumb?.onClear) activeDetailCrumb.onClear();
    setActiveDetailCrumb(null);
  };

  // The location crumb (e.g. "Back Bay") returns to the store's root view when
  // the current view has somewhere to go back from.
  const isStoreRootView = activeTab === "By Employee" && !activeDetailCrumb;
  const goToStoreRoot = () => {
    clearActiveDetail();
    setActiveTab("By Employee");
  };

  function handleTabButtonPress(target) {
    // Switching tabs unmounts the current child (dropping its selection); clear
    // the drilldown crumb so the breadcrumb doesn't trail a stale detail.
    setActiveDetailCrumb(null);
    setActiveTab(target);
  }

  const [openAssignTrackToEmployeeModal, setOpenAssignTrackToEmployeeModal] =
    React.useState(false);
  // Track pre-selected when the manager clicks the "+" on a specific track
  // column (null when opened from the generic "Assign track" button).
  const [preselectedTrackForAssign, setPreselectedTrackForAssign] =
    React.useState(null);
  const handleOpenAssignTrackToEmployeeModal = () =>
    setOpenAssignTrackToEmployeeModal(true);
  const handleCloseAssignTrackToEmployeeModal = () => {
    setOpenAssignTrackToEmployeeModal(false);
    setPreselectedTrackForAssign(null);
  };

  // Bulk-assign a single track to many team members (opened from a track
  // column header). Holds the clicked track column.
  const [openBulkAssignTrackModal, setOpenBulkAssignTrackModal] =
    React.useState(false);
  const [bulkAssignTrack, setBulkAssignTrack] = React.useState(null);
  const handleCloseBulkAssignTrackModal = () => {
    setOpenBulkAssignTrackModal(false);
    setBulkAssignTrack(null);
  };

  const [openUnassignTrackFromEmployeeModal, setOpenUnassignTrackFromEmployeeModal] =
    React.useState(false);
  const handleOpenUnassignTrackFromEmployeeModal = () =>
    setOpenUnassignTrackFromEmployeeModal(true);
  const handleCloseUnassignTrackFromEmployeeModal = () =>
    setOpenUnassignTrackFromEmployeeModal(false);

  const [openCheckpointReviewModal, setOpenCheckpointReviewModal] =
    React.useState(false);
  const handleOpenCheckpointReviewModal = () =>
    setOpenCheckpointReviewModal(true);
  const handleCloseCheckpointReviewModal = () =>
    setOpenCheckpointReviewModal(false);

  const [modalDataTrack, setModalDataTrack] = React.useState([]);

  const [currentTrack, setCurrentTrack] = React.useState(null);
  const [currentTrackUUID, setCurrentTrackUUID] = React.useState(null);
  const [currentTrackEmployeeName, setCurrentTrackEmployeeName] =
    React.useState("");
  const [selectedEmployee, setSelectedEmployee] = React.useState(null);

  // console.log("CURRENT TRACK", currentTrack);
  // console.log("SELECTED STORE", props.selectedStore.id);

  const [isLoading, setIsLoading] = React.useState(false);

  const [showCompleteTrack, setShowCompleteTrack] = React.useState(false);

  const [searchInput, setSearchInput] = React.useState("");
  const [learningTracks, setLearningTracks] = React.useState([]);
  const [learningTracksLoaded, setLearningTracksLoaded] = React.useState(false);

  // ---- Matrix toolbar: group / sort / filter ----
  const [groupByRole, setGroupByRole] = React.useState(false);
  const [sortBy, setSortBy] = React.useState("schedule"); // schedule | proficiency | name | completion | attention | last_active
  const [roleFilter, setRoleFilter] = React.useState([]); // main-role ids to keep ([] = all)
  const [statusFilter, setStatusFilter] = React.useState([]); // status keys to keep ([] = all)
  const [trackFilter, setTrackFilter] = React.useState([]); // track uuids to show ([] = all)
  const [collapsedGroups, setCollapsedGroups] = React.useState([]); // role keys collapsed
  const [viewMenuAnchor, setViewMenuAnchor] = React.useState(null);
  const [filterMenuAnchor, setFilterMenuAnchor] = React.useState(null);

  const SORT_OPTIONS = [
    { value: "schedule", label: "Schedule order" },
    { value: "proficiency", label: "Proficiency" },
    { value: "name", label: "Name (A–Z)" },
    { value: "completion", label: "Avg completion" },
    { value: "attention", label: "Needs attention" },
    { value: "last_active", label: "Last active" },
  ];
  // "Not assigned" excluded: it's per-track, not a meaningful per-employee
  // filter (almost everyone has some unassigned track).
  const STATUS_FILTER_OPTIONS = STATUS_LEGEND_ORDER.filter(
    (key) => key !== "not_assigned"
  ).map((key) => ({
    value: key,
    label: TRAINING_STATUS_META[key].label,
  }));

  // Per-store role colors (same payload the schedule uses) so team-member
  // avatars are tinted by each person's main role. We hold the matrix behind
  // the loading gate until this resolves so avatars don't paint gray and then
  // pop to their color.
  const [roleNames, setRoleNames] = React.useState([]);
  const [roleNamesLoaded, setRoleNamesLoaded] = React.useState(false);
  React.useEffect(() => {
    const storeId = props.selectedStore?.id;
    if (!storeId) {
      setRoleNamesLoaded(true);
      return;
    }
    let cancelled = false;
    setRoleNamesLoaded(false);
    (async () => {
      try {
        const axiosConfig = await getDefaultAxiosConfig();
        const response = await axios.get(
          `${API_get_role_names}${storeId}`,
          axiosConfig
        );
        if (!cancelled) setRoleNames(response.data || []);
      } catch (err) {
        // Non-fatal: avatars fall back to the default color.
      } finally {
        if (!cancelled) setRoleNamesLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [props.selectedStore?.id]);

  // Form submissions awaiting a manager's grade at this store — surfaced in the
  // "Review Pending Actions" modal alongside blocked enrollments (a pending grade
  // is a manager to-do whether or not the form gates the learner).
  const [pendingFormGrades, setPendingFormGrades] = React.useState([]);
  const [pendingGradesRefresh, setPendingGradesRefresh] = React.useState(0);
  React.useEffect(() => {
    const storeId = props.selectedStore?.id;
    if (!storeId || !props.homeCompany) {
      setPendingFormGrades([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await getRightworkAPI(API_get_pending_form_grades_for_store, {
          company_id: props.homeCompany,
          store_id: storeId,
        });
        if (!cancelled) {
          setPendingFormGrades(res.data?.redux?.payload?.pending_form_grades || []);
        }
      } catch (e) {
        if (!cancelled) setPendingFormGrades([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [props.selectedStore?.id, props.homeCompany, pendingGradesRefresh]);

  const getEmployeeAvatarColor = (employee) => {
    const mainRole = (employee?.store_team_member_roles || []).find(
      (r) => r.main_role_ind === 1
    );
    return getCardColor2(mainRole?.scheduled_role_obj?.id, roleNames, false);
  };

  // Maps of main-role avatar color so the By Track / By Record child views can
  // tint avatars without re-deriving roles. By Track keys employees by
  // store_team_member id; By Record's submissions key by user id.
  const employeeAvatarColorById = React.useMemo(() => {
    const map = {};
    (props.storeDetailTrainingDataByEmployee || []).forEach((emp) => {
      map[emp.id] = getEmployeeAvatarColor(emp);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.storeDetailTrainingDataByEmployee, roleNames]);

  const employeeAvatarColorByUserId = React.useMemo(() => {
    const map = {};
    (props.storeDetailTrainingDataByEmployee || []).forEach((emp) => {
      if (emp?.user?.id != null) map[emp.user.id] = getEmployeeAvatarColor(emp);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.storeDetailTrainingDataByEmployee, roleNames]);

  // Frozen-column scroll shadow + responsive column sizing are shared with the
  // Location x Track matrix (see trackMatrixShared). visibleTrackColumns is
  // derived further down, so column sizing is wired up after that.
  const { handleMatrixScroll, renderStickyEdgeShadow } = useStickyEdgeShadow();

  const getEmployeeTotalStars = React.useCallback((employee) => {
    const ratings = (employee?.store_team_member_roles || [])
      .map((role) => role?.training_proficiency_rating)
      .filter((rating) => typeof rating === "number" && !Number.isNaN(rating));

    if (!ratings.length) return 0;
    return ratings.reduce((sum, rating) => sum + rating, 0);
  }, []);

  const getEmployeeMainRole = (employee) =>
    (employee?.store_team_member_roles || []).find(
      (r) => r.main_role_ind === 1
    )?.scheduled_role_obj || null;

  // Track-based sorts/aggregates must reflect only the columns the user can see.
  // When a track filter is active, scope each employee's enrollments to the
  // selected track UUIDs so the row order matches the visible matrix instead of
  // being driven by hidden tracks' progress or blockers.
  const trackFilterSet = React.useMemo(() => new Set(trackFilter), [trackFilter]);

  const scopeEnrollmentsToVisibleTracks = React.useCallback(
    (employee) => {
      const enrollments = employee?.user?.learning_track_enrollments || [];
      if (!trackFilterSet.size) return enrollments;
      return enrollments.filter((e) => trackFilterSet.has(e?.track?.uuid));
    },
    [trackFilterSet]
  );

  const getEmployeeAvgCompletion = React.useCallback(
    (employee) => {
      const enrollments = scopeEnrollmentsToVisibleTracks(employee);
      if (!enrollments.length) return 0;
      return (
        enrollments.reduce((s, e) => s + getTrackProgress(e), 0) /
        enrollments.length
      );
    },
    [scopeEnrollmentsToVisibleTracks]
  );

  const getEmployeeAttentionCount = React.useCallback(
    (employee) => {
      const enrollments = scopeEnrollmentsToVisibleTracks(employee);
      return enrollments.reduce((c, e) => {
        const k = getTrainingStatusKey(e);
        return c + (k === "overdue" || k === "blocked" ? 1 : 0);
      }, 0);
    },
    [scopeEnrollmentsToVisibleTracks]
  );

  // Distinct main roles across the store — powers the role filter + grouping.
  const availableRoles = React.useMemo(() => {
    const map = new Map();
    (props.storeDetailTrainingDataByEmployee || []).forEach((emp) => {
      const role = getEmployeeMainRole(emp);
      if (role?.id != null && !map.has(role.id)) map.set(role.id, role.role_name);
    });
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) =>
      (a.name || "").localeCompare(b.name || "")
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.storeDetailTrainingDataByEmployee]);

  // Per-store role sort order (same source the schedule uses) so role groups
  // appear in the organizational order rather than alphabetically.
  const roleSortIndexById = React.useMemo(() => {
    const map = {};
    (roleNames || []).forEach((r) => {
      if (r?.id != null) map[r.id] = r.sort_index;
    });
    return map;
  }, [roleNames]);

  // Derived in render (not via state + effect) so the row list and the
  // "N team members" count never lag a frame behind the data.
  const filteredEmployees = React.useMemo(() => {
    const lowercasedInput = searchInput.toLowerCase();
    const roleSet = new Set(roleFilter);
    const statusSet = new Set(statusFilter);
    const trackSet = new Set(trackFilter);
    const byName = (a, b) =>
      `${a.first_name || ""} ${a.last_name || ""}`
        .trim()
        .localeCompare(`${b.first_name || ""} ${b.last_name || ""}`.trim());
    const parseTs = (v) => {
      const t = v ? new Date(v).getTime() : NaN;
      return Number.isNaN(t) ? -Infinity : t;
    };
    const compare = (a, b) => {
      if (sortBy === "name") return byName(a, b);
      if (sortBy === "completion") {
        const d = getEmployeeAvgCompletion(b) - getEmployeeAvgCompletion(a);
        return d !== 0 ? d : byName(a, b);
      }
      if (sortBy === "attention") {
        const d = getEmployeeAttentionCount(b) - getEmployeeAttentionCount(a);
        return d !== 0 ? d : byName(a, b);
      }
      if (sortBy === "last_active") {
        const d =
          parseTs(b.latest_section_completion) -
          parseTs(a.latest_section_completion);
        return d !== 0 ? d : byName(a, b);
      }
      const d = getEmployeeTotalStars(b) - getEmployeeTotalStars(a);
      return d !== 0 ? d : byName(a, b);
    };
    const filtered = (props.storeDetailTrainingDataByEmployee || [])
      .filter((employee) =>
        `${employee.first_name} ${employee.last_name}`
          .toLowerCase()
          .includes(lowercasedInput)
      )
      .filter((employee) => {
        if (!roleSet.size) return true;
        const role = getEmployeeMainRole(employee);
        return role?.id != null && roleSet.has(role.id);
      })
      .filter((employee) => {
        if (!statusSet.size) return true;
        // When a track filter is active, only the selected track columns are
        // visible, so the status check must be scoped to those enrollments —
        // otherwise an employee overdue only on a hidden track would still
        // match (e.g. "Track A + Overdue" wrongly including a Track-B overdue).
        const enrollments = employee?.user?.learning_track_enrollments || [];
        const scoped = trackSet.size
          ? enrollments.filter((e) => trackSet.has(e?.track?.uuid))
          : enrollments;
        return scoped.some((e) => statusSet.has(getTrainingStatusKey(e)));
      });
    // "schedule" keeps the backend's sort_index order (same as the schedule's
    // organizational view); everything else re-sorts.
    return sortBy === "schedule" ? filtered : filtered.sort(compare);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchInput,
    roleFilter,
    statusFilter,
    trackFilter,
    sortBy,
    props.storeDetailTrainingDataByEmployee,
    getEmployeeTotalStars,
    getEmployeeAvgCompletion,
    getEmployeeAttentionCount,
  ]);

  // Flat list of rows for the body: when grouping is on, group headers are
  // interleaved and a collapsed group hides its employees.
  const displayRows = React.useMemo(() => {
    if (!groupByRole) {
      return filteredEmployees.map((employee) => ({ type: "employee", employee }));
    }
    const groups = new Map();
    filteredEmployees.forEach((employee) => {
      const role = getEmployeeMainRole(employee);
      const key = role?.id != null ? role.id : "__none__";
      if (!groups.has(key)) groups.set(key, { key, role, employees: [] });
      groups.get(key).employees.push(employee);
    });
    const sortedGroups = Array.from(groups.values()).sort((a, b) => {
      if (!a.role) return 1;
      if (!b.role) return -1;
      const aIdx = roleSortIndexById[a.role.id];
      const bIdx = roleSortIndexById[b.role.id];
      const aHas = aIdx != null;
      const bHas = bIdx != null;
      if (aHas && bHas && aIdx !== bIdx) return aIdx - bIdx;
      if (aHas && !bHas) return -1;
      if (!aHas && bHas) return 1;
      return (a.role.role_name || "").localeCompare(b.role.role_name || "");
    });
    const rows = [];
    sortedGroups.forEach((g) => {
      rows.push({
        type: "group",
        key: g.key,
        role: g.role,
        count: g.employees.length,
      });
      if (!collapsedGroups.includes(g.key)) {
        g.employees.forEach((employee) =>
          rows.push({ type: "employee", employee })
        );
      }
    });
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupByRole, filteredEmployees, collapsedGroups, roleSortIndexById]);

  const activeFilterCount =
    roleFilter.length + statusFilter.length + trackFilter.length;
  const viewActive = groupByRole || sortBy !== "schedule";

  const clearAllFilters = () => {
    setRoleFilter([]);
    setStatusFilter([]);
    setTrackFilter([]);
  };

  const toggleInArray = (setter, value) =>
    setter((prev) =>
      prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value]
    );

  const toggleGroupCollapse = (key) =>
    setCollapsedGroups((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );

  const renderRadioMenuItem = (key, label, active, onClick) => (
    <MenuItem key={key} onClick={onClick} sx={{ py: 0.75 }}>
      <ListItemText
        primaryTypographyProps={{
          fontSize: 14,
          fontWeight: active ? 600 : 400,
          color: active ? "#6d28d9" : "#334155",
        }}
      >
        {label}
      </ListItemText>
      {active && <CheckIcon sx={{ color: "#6d28d9", fontSize: 18, ml: 1 }} />}
    </MenuItem>
  );

  const renderCheckboxMenuItem = (key, label, checked, onClick) => (
    <MenuItem key={key} onClick={onClick} sx={{ py: 0.25 }}>
      <Checkbox
        checked={checked}
        size="small"
        sx={{ color: "#6d28d9", "&.Mui-checked": { color: "#6d28d9" }, py: 0.5 }}
      />
      <ListItemText primaryTypographyProps={{ fontSize: 14, color: "#334155" }}>
        {label}
      </ListItemText>
    </MenuItem>
  );

  const menuSectionLabel = (text) => (
    <div className="px-4 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
      {text}
    </div>
  );

  const fetchLearningTracksForMatrix = React.useCallback(async () => {
    if (!props.homeCompany) {
      setLearningTracks([]);
      setLearningTracksLoaded(true);
      return;
    }

    try {
      const res = await getRightworkAPI(API_get_learning_tracks, {
        company_id: props.homeCompany,
      });

      if (res.status === 200) {
        setLearningTracks(res.data.redux?.payload?.learning_tracks || []);
      }
    } catch (error) {
      console.error("Error fetching learning tracks for employee matrix:", error);
      setLearningTracks([]);
    } finally {
      setLearningTracksLoaded(true);
    }
  }, [props.homeCompany]);

  React.useEffect(() => {
    fetchLearningTracksForMatrix();
  }, [fetchLearningTracksForMatrix]);

  const default_data_section = {
    created_at: null,
    name: "",
    updated_at: null,
    uuid: null,
    as_json: [],
  };
  const [modalDataSection, setModalDataSection] =
    React.useState(default_data_section);

  const fetchTrackDetails = async (
    showLoading,
    learning_track_uuid,
    store_team_member_id
  ) => {
    setIsLoading(showLoading);
    let api_params = {
      company_id: props.homeCompany,
      learning_track_uuid: learning_track_uuid,
      store_team_member_id: store_team_member_id,
      language: localStorage.getItem('preferredLanguage') || "english",
    };
    let api = API_get_learning_track_enrollment_detail_for_employee;
    try {
      const res = await getRightworkAPI(api, api_params);
      if (res.status === 200) {
        setModalDataTrack(res.data.redux?.payload.learning_track);
        setIsLoading(false);
      }
    } catch (error) {
      throw Error(error);
    }
  };

  // ----------- POST APIs ----------- //

  // [POST] api/lms_info/post_learning_section_completed
  // * company_id
  // * learning_section_uuid

  const post_learning_section_completed = async (
    learning_section_uuid,
    store_team_member_id
  ) => {
    // Prepare data to send
    const requestData = {
      company_id: props.homeCompany,
      learning_section_uuid: learning_section_uuid,
      store_team_member_id: store_team_member_id,
    };

    try {
      toast.loading("Completing Section...", { autoClose: false });

      const res = await postRightworkAPI(
        API_post_learning_section_completed,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Section Completed Successfully", { autoClose: 3000 });

        fetchTrackDetails(false, currentTrackUUID, selectedEmployee.id);
        props.fetchStoreDetailTrainingDataByEmployee(
          false,
          props.selectedStore.id
        );
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
        // console.log("Error Info", error.response?.data?.system?.data);
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };

  // [POST] api/lms_info/post_learning_block_user_passed
  // * company_id
  // * learning_quiz_uuid

  const post_learning_block_user_passed = async (
    learning_block_uuid,
    is_passed,
    store_team_member_id,
    score = null,
    question_responses = null
  ) => {
    // Prepare data to send
    const requestData = {
      company_id: props.homeCompany,
      learning_block_uuid: learning_block_uuid,
      is_passed: is_passed,
      store_team_member_id: store_team_member_id,
    };

    // Add optional parameters if provided
    if (score !== null) {
      requestData.score = score;
    }
    if (question_responses !== null && question_responses.length > 0) {
      requestData.question_responses = JSON.stringify(question_responses);
    }

    try {
      // toast.loading("Checking Quiz Score...", { autoClose: false });

      const res = await postRightworkAPI(
        API_post_learning_block_user_passed,
        requestData
      );

      if (res.status === 200) {
        // toast.dismiss();
        // toast.success("Quiz Passed Successfully", { autoClose: 3000 });

        fetchTrackDetails(false, currentTrackUUID, selectedEmployee.id);
        props.fetchStoreDetailTrainingDataByEmployee(
          false,
          props.selectedStore.id
        );

        // Return the response data including attempt_uuid if present
        return res.data.redux?.payload;
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        // toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
        // console.log("Error Info", error.response?.data?.system?.data);
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
      return { error_message: error.response?.data?.system?.message || "Request Failed" };
    }
  };

  // console.log("STORE DETAIL TRAINING BY EMPLOYEE", props.storeDetailTrainingDataByEmployee);

  const filteredEmployeesBlocked = props.storeDetailTrainingDataByEmployee
    ?.map((employee) => {
      // Filter the learning_track_enrollments for each employee
      // Only include enrollments that are blocked AND have a valid blocked_by_type
      const filteredEnrollmentsBlocked =
        employee.user?.learning_track_enrollments?.filter(
          (enrollment) => enrollment.is_blocked && enrollment.blocked_by_type
        );

      // Return a new object with the filtered enrollments
      return {
        ...employee,
        user: {
          ...employee?.user,
          learning_track_enrollments: filteredEnrollmentsBlocked,
        },
      };
    })
    ?.filter(
      (employee) => employee?.user?.learning_track_enrollments?.length > 0
    );

  // Calculate total number of blocked enrollments across all employees
  const totalBlockedEnrollments = filteredEmployeesBlocked?.reduce(
    (sum, employee) => sum + (employee?.user?.learning_track_enrollments?.length || 0),
    0
  ) || 0;

  // console.log("FILTER EMPLOYEES", filteredEmployeesBlocked);

  const exportEmployeeProgressCSV = () => {
    const rows = [];

    // Build one CSV row for an (employee, track) pairing. A null enrollment
    // yields a "Not assigned" row so the export matches the matrix, where an
    // unenrolled track still shows a not-assigned cell. `trackName` names the
    // track for not-assigned rows; for real enrollments the enrollment's own
    // track name is used.
    const buildRow = (name, lastActivity, trackName, enrollment) => {
      if (!enrollment) {
        return {
          "Employee Name": name,
          "Track Name": trackName,
          Status: "Not assigned",
          "Sections Completed": "",
          "Total Sections": "",
          "% Complete": "",
          "Due Date": "",
          Overdue: "",
          "Last Activity": lastActivity,
        };
      }
      // Use the same progress helper as the grid so the %, Status, and
      // Overdue columns agree with each other and with what's on screen.
      const pct = Math.round(getTrackProgress(enrollment));
      const isCompleted = pct >= 100;
      const dueDate = enrollment.effective_due_date || "";
      let overdue = "";
      if (dueDate && !isCompleted) {
        const now = new Date();
        const todayUTC = Date.UTC(
          now.getUTCFullYear(),
          now.getUTCMonth(),
          now.getUTCDate()
        );
        overdue = dueDate < todayUTC ? "Yes" : "No";
      }
      return {
        "Employee Name": name,
        "Track Name": enrollment.track.name,
        Status:
          TRAINING_STATUS_META[getTrainingStatusKey(enrollment)]?.label || "",
        "Sections Completed": enrollment.sections_completed_count,
        "Total Sections": enrollment.total_sections_count,
        "% Complete": `${pct}%`,
        "Due Date": dueDate
          ? new Date(dueDate).toLocaleDateString("en-US", {
              timeZone: "UTC",
            })
          : "",
        Overdue: overdue,
        "Last Activity": lastActivity,
      };
    };

    filteredEmployees.forEach((employee) => {
      const name = formatName(
        [employee.first_name, employee.last_name].filter(Boolean).join(" ")
      );
      const lastActivity = employee.latest_section_completion || "";

      if (trackFilter.length) {
        // Track filter active: mirror the matrix exactly — emit one row per
        // visible (selected) track, marking it "Not assigned" (and still
        // naming the track) when the employee has no enrollment in it. This
        // avoids collapsing a scoped-empty list into a single "No tracks
        // assigned" row that would drop the selected track names and falsely
        // imply the employee has no enrollments at all.
        visibleTrackColumns.forEach((track) => {
          rows.push(
            buildRow(
              name,
              lastActivity,
              track.name,
              getEnrollmentForTrack(employee, track.uuid)
            )
          );
        });
      } else {
        const enrollments = employee?.user?.learning_track_enrollments || [];
        if (enrollments.length === 0) {
          rows.push(buildRow(name, lastActivity, "No tracks assigned", null));
        } else {
          enrollments.forEach((enrollment) =>
            rows.push(buildRow(name, lastActivity, null, enrollment))
          );
        }
      }
    });

    if (rows.length === 0) return;
    const csv = objectToCSV(rows);
    const storeName = props.selectedStore.store_name || "store";
    downloadCSV(csv, `employee_progress_${storeName.replace(/\s+/g, "_")}.csv`);
  };

  const trainingTrackColumns = React.useMemo(() => {
    const tracksByUUID = new Map();
    const assignedCountsByUUID = new Map();

    (props.storeDetailTrainingDataByEmployee || []).forEach((employee) => {
      (employee?.user?.learning_track_enrollments || []).forEach((enrollment) => {
        const track = enrollment?.track;
        if (!track?.uuid) return;

        assignedCountsByUUID.set(
          track.uuid,
          (assignedCountsByUUID.get(track.uuid) || 0) + 1
        );

        if (!tracksByUUID.has(track.uuid)) {
          tracksByUUID.set(track.uuid, {
            uuid: track.uuid,
            name: track.name || "Untitled track",
            assignment_type: track.assignment_type || "track",
            assignedCount: 0,
          });
        }
      });
    });

    (learningTracks || []).forEach((track) => {
      if (!track?.uuid) return;
      // Hide draft tracks from the matrix; published + legacy (NULL) show.
      if (track.status === "draft") return;
      tracksByUUID.set(track.uuid, {
        uuid: track.uuid,
        name: track.name || "Untitled track",
        assignment_type: track.assignment_type || "track",
        // Carried so the bulk-assign modal can show the default due date.
        completion_due_date: track.completion_due_date ?? null,
        completion_due_days_after_enrollment:
          track.completion_due_days_after_enrollment ?? null,
        assignedCount: 0,
      });
    });

    assignedCountsByUUID.forEach((assignedCount, trackUUID) => {
      const existing = tracksByUUID.get(trackUUID);
      tracksByUUID.set(trackUUID, {
        ...(existing || {
          uuid: trackUUID,
          name: "Untitled track",
          assignment_type: "track",
        }),
        assignedCount,
      });
    });

    return Array.from(tracksByUUID.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [learningTracks, props.storeDetailTrainingDataByEmployee]);

  // Track columns actually shown (track filter hides the rest; [] = all).
  const visibleTrackColumns = React.useMemo(() => {
    if (!trackFilter.length) return trainingTrackColumns;
    const keep = new Set(trackFilter);
    return trainingTrackColumns.filter((t) => keep.has(t.uuid));
  }, [trainingTrackColumns, trackFilter]);

  // Responsive track-column sizing (diagonal <-> horizontal header switch) is
  // shared with the Location x Track matrix.
  const { matrixScrollRef, trackColumnWidth, useHorizontalHeader } =
    useTrackColumnSizing(visibleTrackColumns.length);

  const openAssignedTrack = (employee, enrollment) => {
    setCurrentTrackEmployeeName(
      `${employee.first_name || ""} ${employee.last_name || ""}`.trim()
    );
    setCurrentTrack(enrollment);
    setCurrentTrackUUID(enrollment.track.uuid);
    fetchTrackDetails(true, enrollment.track.uuid, employee.id);
    setShowCompleteTrack(true);
    setSelectedEmployee(employee);
  };

  const openAssignTrackForEmployee = (employee, track = null) => {
    setSelectedEmployee(employee);
    setPreselectedTrackForAssign(track);
    handleOpenAssignTrackToEmployeeModal();
  };

  const openBulkAssignForTrack = (track) => {
    setBulkAssignTrack(track);
    setOpenBulkAssignTrackModal(true);
  };

  const openUnassignTrackForEmployee = (employee, enrollment) => {
    setSelectedEmployee(employee);
    setCurrentTrack(enrollment.track);
    handleOpenUnassignTrackFromEmployeeModal();
  };

  const getEnrollmentForTrack = (employee, trackUUID) => {
    return (employee?.user?.learning_track_enrollments || []).find(
      (enrollment) => enrollment?.track?.uuid === trackUUID
    );
  };

  const renderTrainingStatusTooltip = ({
    employeeName,
    track,
    enrollment,
    statusMeta,
    progress,
  }) => {
    if (!enrollment) {
      return (
        <div>
          <div className="font-semibold">{track.name}</div>
          <div>{employeeName}</div>
          <div>{statusMeta.label}</div>
          <div>Click to assign track</div>
        </div>
      );
    }

    const dueDate = formatEnrollmentDueDate(enrollment.effective_due_date);

    return (
      <div>
        <div className="font-semibold">{track.name}</div>
        <div>{employeeName}</div>
        <div>{statusMeta.label}</div>
        <div>
          {enrollment.sections_completed_count} of{" "}
          {enrollment.total_sections_count} sections complete
        </div>
        <div>{Math.round(progress)}% complete</div>
        {dueDate && <div>Due {dueDate}</div>}
        {enrollment.is_blocked && enrollment.blocked_by_type && (
          <div>
            Blocked by{" "}
            {{
              quiz: "failed quiz",
              checkpoint: "checkpoint",
              manager_input: "manager input",
              manager_form: "manager form",
            }[enrollment.blocked_by_type] ||
              enrollment.blocked_by_type.replace(/_/g, " ")}
          </div>
        )}
      </div>
    );
  };

  // Hover popover anchored to the team-member avatar/name. Surfaces the detail
  // that used to live in its own columns: last activity, plus the per-role
  // proficiency breakdown when the company drives proficiency from the LMS
  // (hidden otherwise — that number is meaningless here and wouldn't match the
  // schedule).
  const renderEmployeeInfoPopover = (employee) => {
    const employeeName = formatName(
      [employee.first_name, employee.last_name].filter(Boolean).join(" ")
    );
    const stmRoles = employee?.store_team_member_roles || [];
    const showProficiency =
      props.proficiencyFromLMSEnabled && stmRoles.length > 0;
    // A team member can have stale `main_role_ind = 1` on more than one role
    // row. The schedule resolves a single primary role; mirror that here by
    // treating only the first flagged role as "Main" (same one the avatar
    // color uses) so we don't render multiple MAIN badges.
    const mainRoleObj = stmRoles.find((r) => r.main_role_ind === 1) || null;

    const roles = showProficiency
      ? [...stmRoles].sort((a, b) => {
          if (
            a.scheduled_role_obj?.role_type_id ===
            b.scheduled_role_obj?.role_type_id
          ) {
            return a.scheduled_role_obj?.role_name.localeCompare(
              b.scheduled_role_obj?.role_name
            );
          }
          return (
            a.scheduled_role_obj?.role_type_id -
            b.scheduled_role_obj?.role_type_id
          );
        })
      : [];

    // Average completion across the tracks this employee is enrolled in.
    // Scoped to the visible track columns when a track filter is active so the
    // tooltip agrees with the sort and the matrix (same metric as
    // getEmployeeAvgCompletion).
    const enrollments = scopeEnrollmentsToVisibleTracks(employee);
    const avgCompletion = enrollments.length
      ? Math.round(getEmployeeAvgCompletion(employee))
      : 0;

    return (
      <div className="w-64 p-3 text-left">
        <div className="mb-2 text-sm font-semibold text-slate-900">
          {employeeName}
        </div>

        <div className="mb-1 flex items-center justify-between text-xs text-slate-600">
          <span>Avg track completion</span>
          <span className="font-medium text-slate-900">
            {enrollments.length ? `${avgCompletion}%` : "—"}
          </span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
          <div
            className="h-full rounded-full bg-violet-600"
            style={{ width: `${enrollments.length ? avgCompletion : 0}%` }}
          />
        </div>
        <div className="mt-1 text-xxs text-slate-400">
          {enrollments.length} track{enrollments.length === 1 ? "" : "s"}{" "}
          assigned
        </div>

        <div className="mt-3 flex items-center gap-1.5 border-t border-slate-100 pt-2.5 text-xs text-slate-600">
          <CalendarTodayIcon style={{ fontSize: "13px" }} />
          {employee?.latest_section_completion ? (
            <span>
              Last active{" "}
              {formatDateReadableWithDayOfWeek(
                employee?.latest_section_completion
              )}
            </span>
          ) : (
            <span className="text-slate-400">No activity yet</span>
          )}
        </div>

        {showProficiency && (
          <div className="mt-3 border-t border-slate-100 pt-2.5">
            <div className="mb-2 text-xs font-semibold text-slate-900">
              Role Proficiency
            </div>
            <div className="space-y-1.5">
              {roles.map((role, roleIndex) => (
                <div
                  key={role.id || roleIndex}
                  className="flex items-center justify-between gap-4 text-xs"
                >
                  <span className="flex items-center gap-1.5 text-slate-700">
                    {role?.scheduled_role_obj?.role_name}
                    {role === mainRoleObj && (
                      <span className="inline-flex items-center rounded-full bg-violet-100 px-1.5 pb-[2px] pt-[3px] text-[10px] font-semibold uppercase leading-none tracking-wide text-violet-700">
                        Main
                      </span>
                    )}
                  </span>
                  <Rating
                    value={role?.training_proficiency_rating || 0}
                    precision={0.5}
                    readOnly
                    max={props.proficiencyStarsCount}
                    size="small"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTrackStatusCell = (employee, track) => {
    const employeeName = formatName(
      [employee.first_name, employee.last_name].filter(Boolean).join(" ")
    );
    const enrollment = getEnrollmentForTrack(employee, track.uuid);
    const progress = getTrackProgress(enrollment);
    const statusKey = getTrainingStatusKey(enrollment);
    const statusMeta = TRAINING_STATUS_META[statusKey];
    const isCompleted = statusKey === "complete";

    return (
      <td
        key={`${employee.id}-${track.uuid}`}
        className="border-l border-slate-100 px-1.5 py-1.5 text-center align-middle"
        style={{ width: trackColumnWidth, minWidth: trackColumnWidth }}
      >
        {/* Full-width status bar (flexes to fill the column, matching the
            Location matrix), still interactive: click to open/assign, hover to
            reveal the unassign affordance. */}
        <div className="group relative">
          <Tooltip
            title={renderTrainingStatusTooltip({
              employeeName,
              track,
              enrollment,
              statusMeta,
              progress,
            })}
            arrow
          >
            <button
              type="button"
              className={`flex h-9 w-full items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-200 ${statusMeta.circleClass}`}
              onClick={() => {
                if (enrollment) {
                  openAssignedTrack(employee, enrollment);
                } else {
                  openAssignTrackForEmployee(employee, track);
                }
              }}
              aria-label={`${employeeName} ${track.name}: ${statusMeta.label}${
                enrollment ? ` ${Math.round(progress)}%` : ""
              }`}
            >
              {enrollment ? (
                <span className="text-[10px] font-semibold leading-none">
                  {Math.round(progress)}%
                </span>
              ) : (
                <AddIcon style={{ fontSize: "18px" }} />
              )}
            </button>
          </Tooltip>

          {enrollment && !isCompleted && (
            <Tooltip title={`Unassign ${track.name}`} arrow>
              <button
                type="button"
                className="absolute -right-1.5 -top-1.5 hidden h-5 w-5 items-center justify-center rounded-full border border-red-200 bg-white text-red-500 shadow-sm transition-colors hover:bg-red-50 group-hover:flex focus-visible:flex focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-100"
                onClick={(event) => {
                  event.stopPropagation();
                  openUnassignTrackForEmployee(employee, enrollment);
                }}
                aria-label={`Unassign ${track.name} from ${employeeName}`}
              >
                <CloseIcon style={{ fontSize: "13px" }} />
              </button>
            </Tooltip>
          )}
        </div>
      </td>
    );
  };

  return (
    <>
      {!isLoading && roleNamesLoaded && learningTracksLoaded ? (
        <div className="min-h-screen bg-white">
          {!showCompleteTrack ? (
            <>
              {/* Header Section */}
              <div className="bg-white border-b border-gray-200">
                <div className="px-6 py-8">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="mb-4">
                        <TrainingBreadcrumb
                          crumbs={[
                            {
                              label: "Training Overview",
                              onClick: props.onBackToOverview,
                            },
                            {
                              label: props.selectedStore.store_name,
                              onClick: isStoreRootView
                                ? undefined
                                : goToStoreRoot,
                            },
                            {
                              label: activeTab,
                              onClick: activeDetailCrumb
                                ? clearActiveDetail
                                : undefined,
                            },
                            ...(activeDetailCrumb
                              ? [{ label: activeDetailCrumb.label }]
                              : []),
                          ]}
                        />
                      </div>
                      <h1 className="text-2xl font-bold text-slate-900 mb-1">
                        {props.selectedStore.store_name}
                      </h1>
                      <p className="text-sm text-slate-600">
                        Training progress and management for team members
                      </p>
                    </div>
                    <div className="w-96">
                      <TabsThree
                        activeTab={activeTab}
                        handleTabButtonPress={handleTabButtonPress}
                        tab1="By Employee"
                        tab2="By Track"
                        tab3="By Record"
                      />
                    </div>
                  </div>

                </div>
              </div>

              {/* Employee Table */}
              {activeTab === "By Employee" && (
                <div className="px-6 py-8">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={(e) => setViewMenuAnchor(e.currentTarget)}
                      className={`flex h-[46px] items-center gap-2 whitespace-nowrap rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium transition-colors hover:border-slate-300 hover:bg-slate-50 ${
                        viewActive ? "text-violet-700" : "text-slate-800"
                      }`}
                    >
                      <ViewListOutlinedIcon style={{ fontSize: "18px" }} />
                      View
                      <KeyboardArrowDownIcon
                        className="text-slate-500"
                        style={{ fontSize: "18px" }}
                      />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
                      className={`flex h-[46px] items-center gap-2 whitespace-nowrap rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium transition-colors hover:border-slate-300 hover:bg-slate-50 ${
                        activeFilterCount > 0 ? "text-violet-700" : "text-slate-800"
                      }`}
                    >
                      <FilterAltOutlinedIcon style={{ fontSize: "18px" }} />
                      Filter
                      {activeFilterCount > 0 && (
                        <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-violet-100 px-1.5 text-[11px] font-semibold text-violet-700">
                          {activeFilterCount}
                        </span>
                      )}
                      <KeyboardArrowDownIcon
                        className="text-slate-500"
                        style={{ fontSize: "18px" }}
                      />
                    </button>
                    <div className="w-[240px]">
                      <div className="relative">
                        <SearchIcon
                          className="absolute left-3 top-1/2 -translate-y-1/2 transform text-slate-400"
                          style={{ fontSize: "20px" }}
                        />
                        <input
                          className="h-[46px] w-full rounded-lg border border-slate-200 pl-10 pr-4 text-sm outline-none transition-all duration-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                          type="text"
                          placeholder="Search team members..."
                          value={searchInput}
                          onChange={(event) => setSearchInput(event.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    {totalBlockedEnrollments + pendingFormGrades.length > 0 && (
                      <button
                        className="flex h-[46px] items-center gap-2 whitespace-nowrap rounded-lg bg-amber-500 px-5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-amber-600"
                        onClick={handleOpenCheckpointReviewModal}
                      >
                        <WarningIcon style={{ fontSize: "18px" }} />
                        Review Actions ({totalBlockedEnrollments + pendingFormGrades.length})
                      </button>
                    )}
                    <button
                      className="flex h-[46px] items-center gap-2 whitespace-nowrap rounded-lg bg-emerald-600 px-5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-emerald-700"
                      onClick={exportEmployeeProgressCSV}
                    >
                      <DownloadIcon style={{ fontSize: "18px" }} />
                      Export CSV
                    </button>
                  </div>
                </div>

                {/* View menu: grouping + sorting */}
                <Menu
                  anchorEl={viewMenuAnchor}
                  open={Boolean(viewMenuAnchor)}
                  onClose={() => setViewMenuAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  PaperProps={{ sx: { minWidth: 220, mt: 1 } }}
                >
                  {menuSectionLabel("Group by")}
                  {renderRadioMenuItem("group-none", "None", !groupByRole, () =>
                    setGroupByRole(false)
                  )}
                  {renderRadioMenuItem("group-role", "Role", groupByRole, () =>
                    setGroupByRole(true)
                  )}
                  <Divider />
                  {menuSectionLabel("Sort by")}
                  {SORT_OPTIONS.map((opt) =>
                    renderRadioMenuItem(
                      `sort-${opt.value}`,
                      opt.label,
                      sortBy === opt.value,
                      () => setSortBy(opt.value)
                    )
                  )}
                </Menu>

                {/* Filter menu: roles, status, tracks */}
                <Menu
                  anchorEl={filterMenuAnchor}
                  open={Boolean(filterMenuAnchor)}
                  onClose={() => setFilterMenuAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  PaperProps={{ sx: { minWidth: 260, maxHeight: 460, mt: 1 } }}
                  MenuListProps={{ sx: { pt: 0 } }}
                >
                  <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-[11px] font-semibold uppercase leading-5 tracking-wide text-slate-400">
                      Filters
                    </span>
                    {activeFilterCount > 0 && (
                      <button
                        type="button"
                        className="text-xs font-medium leading-5 text-violet-700 hover:text-violet-800"
                        onClick={clearAllFilters}
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                  <Divider />
                  {availableRoles.length > 0 && menuSectionLabel("Role")}
                  {availableRoles.map((role) =>
                    renderCheckboxMenuItem(
                      `role-${role.id}`,
                      role.name,
                      roleFilter.includes(role.id),
                      () => toggleInArray(setRoleFilter, role.id)
                    )
                  )}
                  {availableRoles.length > 0 && <Divider />}
                  {menuSectionLabel("Status")}
                  {STATUS_FILTER_OPTIONS.map((opt) =>
                    renderCheckboxMenuItem(
                      `status-${opt.value}`,
                      opt.label,
                      statusFilter.includes(opt.value),
                      () => toggleInArray(setStatusFilter, opt.value)
                    )
                  )}
                  {trainingTrackColumns.length > 0 && <Divider />}
                  {trainingTrackColumns.length > 0 && menuSectionLabel("Tracks")}
                  {trainingTrackColumns.map((track) =>
                    renderCheckboxMenuItem(
                      `track-${track.uuid}`,
                      track.name,
                      trackFilter.includes(track.uuid),
                      () => toggleInArray(setTrackFilter, track.uuid)
                    )
                  )}
                </Menu>
                <div className="-mx-6 border-y border-slate-200 bg-white">
                  <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 bg-slate-50 px-5 py-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                        Track status matrix
                      </p>
                      <p className="text-xs text-slate-500">
                        {visibleTrackColumns.length} tracks across{" "}
                        {filteredEmployees.length} team members
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                      {STATUS_LEGEND_ORDER.map((statusKey) => {
                        const statusMeta = TRAINING_STATUS_META[statusKey];
                        return (
                          <div
                            key={statusKey}
                            className="flex items-center gap-1.5 text-xs text-slate-600"
                          >
                            <span
                              className={`h-3 w-3 rounded-sm border ${statusMeta.legendClass}`}
                            />
                            <span>{statusMeta.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {trainingTrackColumns.length > 0 ? (
                    <div
                      ref={matrixScrollRef}
                      className="overflow-x-auto"
                      onScroll={handleMatrixScroll}
                    >
                      <table className="w-full table-fixed divide-y divide-slate-200">
                        <thead className="bg-slate-50">
                          <TrackMatrixHeaderRow
                            columns={visibleTrackColumns}
                            trackColumnWidth={trackColumnWidth}
                            useHorizontalHeader={useHorizontalHeader}
                            stickyLabel="Team Member"
                            renderStickyEdgeShadow={renderStickyEdgeShadow}
                            renderColumnMeta={(track) =>
                              `${track.assignedCount} assigned`
                            }
                            onTrackClick={openBulkAssignForTrack}
                          />
                        </thead>
                        <tbody className="divide-y divide-slate-200 bg-white">
                          {filteredEmployees.length > 0 ? (
                            displayRows.map((row, index) => {
                              if (row.type === "group") {
                                const collapsed = collapsedGroups.includes(
                                  row.key
                                );
                                return (
                                  <tr
                                    key={`group-${row.key}`}
                                    className="bg-slate-50"
                                  >
                                    <td className="sticky left-0 z-10 bg-slate-50 py-2">
                                      {renderStickyEdgeShadow()}
                                      <button
                                        type="button"
                                        onClick={() =>
                                          toggleGroupCollapse(row.key)
                                        }
                                        className="flex items-center gap-1.5 px-5 text-left"
                                        style={{ width: STICKY_COL_WIDTH }}
                                      >
                                        {collapsed ? (
                                          <ChevronRightIcon
                                            className="text-slate-400"
                                            style={{ fontSize: "18px" }}
                                          />
                                        ) : (
                                          <ExpandMoreIcon
                                            className="text-slate-400"
                                            style={{ fontSize: "18px" }}
                                          />
                                        )}
                                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                                          {row.role?.role_name || "No role"}
                                        </span>
                                        <span className="text-xs text-slate-400">
                                          {row.count}
                                        </span>
                                      </button>
                                    </td>
                                    <td
                                      aria-hidden="true"
                                      colSpan={visibleTrackColumns.length + 1}
                                      className="bg-slate-50"
                                    />
                                  </tr>
                                );
                              }
                              const employee = row.employee;
                              const employeeName = formatName(
                                [employee.first_name, employee.last_name]
                                  .filter(Boolean)
                                  .join(" ")
                              );

                              return (
                                <tr
                                  key={employee.id || index}
                                  className="group hover:bg-slate-50"
                                >
                                  <td className="sticky left-0 z-10 bg-white py-2 align-middle group-hover:bg-slate-50">
                                    {renderStickyEdgeShadow()}
                                    <div
                                      className="flex min-w-0 items-center gap-2 px-5"
                                      style={{ width: STICKY_COL_WIDTH }}
                                    >
                                      <Tooltip
                                        title={renderEmployeeInfoPopover(
                                          employee
                                        )}
                                        placement="bottom-start"
                                        arrow
                                        slotProps={{
                                          tooltip: {
                                            sx: {
                                              bgcolor: "white",
                                              color: "#0f172a",
                                              p: 0,
                                              maxWidth: "none",
                                              border: "1px solid #e2e8f0",
                                              borderRadius: "12px",
                                              boxShadow:
                                                "0 12px 28px -8px rgba(15,23,42,0.25)",
                                            },
                                          },
                                          arrow: { sx: { color: "white" } },
                                        }}
                                      >
                                        <div className="flex min-w-0 flex-1 items-center gap-2.5">
                                          <div className="shrink-0">
                                            <PersonAvatar
                                              name={employeeName}
                                              bgColor={getEmployeeAvatarColor(
                                                employee
                                              )}
                                              view="daily"
                                            />
                                          </div>
                                          <div className="truncate text-xs font-semibold text-slate-900">
                                            {employeeName}
                                          </div>
                                        </div>
                                      </Tooltip>
                                      <Tooltip title="Assign track" arrow>
                                        <button
                                          type="button"
                                          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-violet-700 transition-colors hover:bg-violet-50 hover:text-violet-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-200"
                                          onClick={() =>
                                            openAssignTrackForEmployee(employee)
                                          }
                                          aria-label={`Assign track to ${employeeName}`}
                                        >
                                          <AddIcon style={{ fontSize: "16px" }} />
                                        </button>
                                      </Tooltip>
                                    </div>
                                  </td>
                                  {visibleTrackColumns.map((track) =>
                                    renderTrackStatusCell(employee, track)
                                  )}
                                  <td
                                    aria-hidden="true"
                                    className="border-l border-slate-100"
                                  />
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td
                                className="px-5 py-10 text-center text-sm text-slate-500"
                                colSpan={2 + visibleTrackColumns.length}
                              >
                                No team members match the current filters.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
                      <InfoIcon
                        className="mb-3 text-slate-400"
                        style={{ fontSize: "28px" }}
                      />
                      <p className="text-sm font-medium text-slate-900">
                        No training tracks assigned yet
                      </p>
                      <p className="mt-1 text-sm text-slate-500">
                        Available tracks will appear here once they are created
                        or assigned.
                      </p>
                    </div>
                  )}
                </div>
                </div>
              )}

              {activeTab === "By Track" && (
                <TrackProgressByTrackDisplay
                  selectedStore={props.selectedStore}
                  storeDetailTrainingDataByEmployee={props.storeDetailTrainingDataByEmployee}
                  employeeAvatarColorById={employeeAvatarColorById}
                  onDetailChange={handleDetailChange}
                />
              )}

              {activeTab === "By Record" && (
                <RecordsDisplay
                  selectedStore={props.selectedStore}
                  homeCompany={props.homeCompany}
                  employeeAvatarColorByUserId={employeeAvatarColorByUserId}
                  onDetailChange={handleDetailChange}
                />
              )}
            </>
          ) : (
            <div className="w-full min-h-screen">
              <CompleteTrack
                currentTrack={currentTrack}
                modalDataTrack={modalDataTrack}
                setModalDataTrack={setModalDataTrack}
                modalDataSection={modalDataSection}
                setModalDataSection={setModalDataSection}
                homeCompany={props.homeCompany}
                showCompleteTrack={showCompleteTrack}
                setShowCompleteTrack={setShowCompleteTrack}
                post_learning_section_completed={post_learning_section_completed}
                post_learning_block_user_passed={post_learning_block_user_passed}
                employeeName={currentTrackEmployeeName}
                selectedEmployee={selectedEmployee}
                userType="Manager"
                language={localStorage.getItem('preferredLanguage') || "english"}
              />
            </div>
          )}
        </div>
      ) : (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <LoadSpinner />
        </div>
      )}
      <AssignTrackToEmployeeModal
        open={openAssignTrackToEmployeeModal}
        handleClose={handleCloseAssignTrackToEmployeeModal}
        post_learning_track_enrollment={props.post_learning_track_enrollment}
        homeCompany={props.homeCompany}
        selectedEmployee={selectedEmployee}
        preselectedTrackUuid={preselectedTrackForAssign?.uuid}
      />
      <BulkAssignTrackModal
        open={openBulkAssignTrackModal}
        handleClose={handleCloseBulkAssignTrackModal}
        post_bulk_learning_track_enrollment={
          props.post_bulk_learning_track_enrollment
        }
        homeCompany={props.homeCompany}
        track={bulkAssignTrack}
        teamMembers={filteredEmployees}
      />
      <UnassignTrackFromEmployeeModal
        open={openUnassignTrackFromEmployeeModal}
        handleClose={handleCloseUnassignTrackFromEmployeeModal}
        post_learning_track_unenrollment={props.post_learning_track_unenrollment}
        homeCompany={props.homeCompany}
        selectedEmployee={selectedEmployee}
        currentTrack={currentTrack}
      />

      <CheckpointReviewModal
        open={openCheckpointReviewModal}
        handleClose={handleCloseCheckpointReviewModal}
        homeCompany={props.homeCompany}
        filteredEmployeesBlocked={filteredEmployeesBlocked}
        pendingFormGrades={pendingFormGrades}
        onPendingGradeResolved={() => setPendingGradesRefresh((n) => n + 1)}
        employeeAvatarColorById={employeeAvatarColorById}
        setCurrentTrackEmployeeName={setCurrentTrackEmployeeName}
        setCurrentTrack={setCurrentTrack}
        setCurrentTrackUUID={setCurrentTrackUUID}
        fetchTrackDetails={fetchTrackDetails}
        setShowCompleteTrack={setShowCompleteTrack}
        setSelectedEmployee={setSelectedEmployee}
      />
    </>
  );
}

export default TrainingStoreDisplay;
